/**
 * @file Foot Facts render — Variant A: email-faithful.
 *
 * Renders the "Your Foot Facts" report with the same sections, copy branches, inline
 * styling, and S3 imagery as the generic Foot Facts email (Klaviyo), driven by a
 * footFacts object computed client-side (see foot-facts-loader.js). The goal is a
 * report that looks near-identical to the email, serving as the consistency reference
 * (and visual parity harness) next to Variant B, the native theme component.
 *
 * Fidelity decisions:
 * - Based on the GENERIC email template only (decision D12) — no Costco content.
 * - Email-only chrome is dropped (decision D7): nav buttons, social footer,
 *   organization address, unsubscribe, and all MSO/Outlook conditional markup.
 * - Greeting is genericized (decision 7.2): "Hi <firstName>" when a name is passed,
 *   "Hi there" otherwise. Gender is optional; without it the email's own
 *   gender-neutral branches are used.
 * - Render-level fixes carried per decision D11 (documented in
 *   docs/projects/foot-facts-web-port/HANDOFF-foot-facts-backend.md):
 *   - bug 2: the no-gender UK size line shows the single unisex UK value instead of
 *     fabricating a "women's" UK size from the men's field;
 *   - bug 3: the "different shoe sizes" test compares the field being displayed.
 * - Copy matches the repo-fixed template texts (e.g. "Your left arch is …" — the
 *   "left left" bug stays fixed). For average arches the summary says "average
 *   arches" — the user-decided canonical copy (2026-07-13), matching what the live
 *   email has always displayed (the template's "average height" branch was dead code).
 *
 * Usage: window.FootFactsRenderEmail.render(footFacts, { gender, firstName })
 * → HTML string. Mount it wherever needed (modal or standalone page, decision D8).
 */
(function () {
  'use strict';

  // Guard against multiple loads.
  if (window.FootFactsRenderEmail) return;

  const S3 = 'https://s3-us-west-1.amazonaws.com/wv-prod-storage/foot-facts';

  // ── Style constants (mirroring the email's inline styles) ─────────────────
  const FONT_HEAD = "'Poppins', Arial, sans-serif";
  const FONT_BODY = "'Muli', Arial, sans-serif";
  const ST = {
    heroTitle: `font-family:${FONT_HEAD}; text-align:center; font-size:48px; line-height:1.2; font-weight:bold; color:#1C1D1D`,
    heroGreeting: `font-family:${FONT_HEAD}; font-size:32px; line-height:34px; font-weight:bold; color:#1C1D1D; text-align:center`,
    heroTeaser: `font-family:${FONT_BODY}; font-size:20px; line-height:28px; font-weight:normal; text-align:center; color:#1C1D1D`,
    heroScroll: `font-family:${FONT_HEAD}; font-size:13px; line-height:23px; font-weight:normal; color:#1C1D1D; text-align:center`,
    sectionTitle: `font-family:${FONT_HEAD}; font-size:34px; font-weight:bold; color:#fff; text-align:center`,
    chartTitle: `font-family:${FONT_HEAD}; font-size:28px; font-weight:bold; color:#fff; text-align:center`,
    summary: `text-align:center; font-family:${FONT_BODY}; font-size:20px; line-height:28px; font-weight:normal; color:#fff`,
    subhead: `text-align:left; font-family:${FONT_HEAD}; font-size:26px; line-height:34px; font-weight:bold; color:#fff`,
    bulletDot: `text-align:left; font-family:${FONT_HEAD}; line-height:24px; font-size:20px; font-weight:normal; color:#fff; vertical-align:top; width:20px`,
    bulletText: `text-align:left; font-family:${FONT_HEAD}; font-size:16px; line-height:24px; font-weight:normal; color:#fff`,
    footHeader: `font-size:16px; text-align:center; font-family:${FONT_BODY}; font-weight:900; font-style:italic; color:#fff`,
    footDescription: `font-size:20px; text-align:center; font-family:${FONT_BODY}; font-weight:900; font-style:italic; color:#fff`,
    scaleLabel: `text-align:center; font-family:${FONT_BODY}; font-size:11px; font-weight:normal; color:#fff; white-space:nowrap`,
    footnote: `text-align:left; font-family:${FONT_HEAD}; font-size:14px; line-height:24px; font-weight:normal; color:#fff`,
    chartLine: `text-align:left; font-family:${FONT_HEAD}; font-size:16px; line-height:24px; font-weight:normal; color:#fff`,
  };

  // ── Small helpers ──────────────────────────────────────────────────────────
  const capfirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const spacer = (h) => `<div style="height:${h}px; font-size:1px; line-height:1px">&nbsp;</div>`;

  // "an average width" / "a narrow" / "a wide" (matches the email's phrasing).
  const widthPhrase = (cls) => (cls === 'average' ? 'an average width' : 'a ' + cls);
  // "an average height" / "a low" / "a high" (arch section phrasing).
  const archPhrase = (cls) => (cls === 'average' ? 'an average height' : 'a ' + cls);
  const lengthCaption = (cls) =>
    cls === 'above_average' ? 'Above average' : cls === 'below_average' ? 'Below average' : capfirst(cls);

  // D11 bug-3 fix: the email always tests usMen; here the comparison matches the field
  // being displayed. (All shoe sizes derive from the same uk value, so the outcomes
  // coincide today — this keeps display and test coupled if that ever changes.)
  function sizesDiffer(ff, gender) {
    const l = ff.leftFoot.shoeSizes;
    const r = ff.rightFoot.shoeSizes;
    if (gender === 'female') return l.usWomen !== r.usWomen;
    if (gender === 'male') return l.usMen !== r.usMen;
    return l.usMen !== r.usMen || l.usWomen !== r.usWomen;
  }
  const maxOf = (ff, key) => Math.max(ff.leftFoot.shoeSizes[key], ff.rightFoot.shoeSizes[key]);

  function bullets(items) {
    const rows = items.map((text) => `
      <tr>
        <td style="${ST.bulletDot}" valign="top">&bull;</td>
        <td style="${ST.bulletText}">${text}</td>
      </tr>
      <tr><td colspan="2" style="height:20px; font-size:1px; line-height:1px">&nbsp;</td></tr>`);
    return `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse">${rows.join('')}</table>`;
  }

  // The five-column foot-comparison row: card | arrow | scale | arrow | card.
  function footCards({ imgLeft, imgRight, imgMaxWidth, arrowColor, leftPct, rightPct, scaleTop, scaleBottom, captionLeft, captionRight }) {
    const card = (header, img, caption) => `
      <td align="center" width="200" style="border-collapse:collapse">
        <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse">
          <tr><td align="center" style="${ST.footHeader}"><strong>${header}</strong></td></tr>
          <tr><td align="center" style="text-align:center">
            <img src="${img}" width="180" style="border:0; height:auto; line-height:100%; width:100%; max-width:${imgMaxWidth}px; display:block; margin-left:auto; margin-right:auto" alt="">
          </td></tr>
          <tr><td align="center" style="${ST.footDescription}">${caption}</td></tr>
        </table>
      </td>`;
    return `
      <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse">
        <tr>
          ${card('Left Foot', imgLeft, captionLeft)}
          <td align="right" style="border-collapse:collapse">
            <img src="${S3}/arrow/${arrowColor}/feet_left_${leftPct}.png" style="border:0; width:auto; height:auto; max-width:none; display:inline-block; vertical-align:middle; line-height:100%" alt="">
          </td>
          <td style="border-collapse:collapse">
            <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse">
              <tr><td align="center" style="${ST.scaleLabel}">${scaleTop}</td></tr>
              <tr><td style="height:5px; font-size:1px; line-height:1px">&nbsp;</td></tr>
              <tr><td align="center" style="text-align:center">
                <img src="${S3}/arrow/measurement_line.png" style="border:0; width:auto; height:auto; max-width:none; display:inline-block; vertical-align:middle; line-height:100%" alt="">
              </td></tr>
              <tr><td style="height:5px; font-size:1px; line-height:1px">&nbsp;</td></tr>
              <tr><td align="center" style="${ST.scaleLabel}">${scaleBottom}</td></tr>
            </table>
          </td>
          <td align="left" style="border-collapse:collapse">
            <img src="${S3}/arrow/${arrowColor}/feet_right_${rightPct}.png" style="border:0; width:auto; height:auto; max-width:none; display:inline-block; vertical-align:middle; line-height:100%" alt="">
          </td>
          ${card('Right Foot', imgRight, captionRight)}
        </tr>
      </table>`;
  }

  function section(bgcolor, inner) {
    return `
      <div style="background-color:${bgcolor}; padding-left:20px; padding-right:20px">
        <div style="margin:0 auto; width:100%; max-width:460px">${inner}</div>
      </div>`;
  }

  // ── HERO ───────────────────────────────────────────────────────────────────
  function heroTeaser(ff, gender) {
    const L = ff.leftFoot;
    const R = ff.rightFoot;
    if (sizesDiffer(ff, gender)) {
      if (gender === 'male') {
        return `Did you know that your left foot is a men’s size ${L.shoeSizes.usMen} and your right foot is a men’s size ${R.shoeSizes.usMen}? Well now you do!`;
      }
      if (gender === 'female') {
        return `Did you know that your left foot is a women’s size ${L.shoeSizes.usWomen} and your right foot is a women’s size ${R.shoeSizes.usWomen}? Well now you do!`;
      }
      return 'Did you know that your left foot is a different shoe size than your right foot?';
    }
    if (L.archHeightClass !== R.archHeightClass) {
      return `Did you know that your left arch is ${L.archHeightClass} and your right arch is ${R.archHeightClass}? Well now you do!`;
    }
    if (L.widthClass !== R.widthClass) {
      return `Did you know that you have ${widthPhrase(L.widthClass)} left foot, and ${widthPhrase(R.widthClass)} right foot? Well now you do!`;
    }
    if (L.length === R.length) {
      return 'Your feet are EXACTLY the same length. We’re impressed!';
    }
    if (ff.lengthRatio > 0) {
      return `Did you know that your left foot is ${ff.lengthRatio}% longer than your right foot? Well now you do!`;
    }
    return `Did you know that your left foot is ${Math.abs(ff.lengthRatio)}% shorter than your right foot? Well now you do!`;
  }

  function hero(ff, opts) {
    const greeting = opts.firstName ? 'Hi ' + escapeHtml(opts.firstName) : 'Hi there';
    return section('#ffffff', `
      ${spacer(40)}
      <div style="${ST.heroTitle}">YOUR FOOT FACTS</div>
      ${spacer(60)}
      <div style="text-align:center">
        <img src="${S3}/footFacts-hero-wiggle.gif" width="169" style="border:0; height:auto; line-height:100%; display:block; margin:0 auto; max-width:169px; width:100%" alt="">
      </div>
      ${spacer(20)}
      <div style="${ST.heroGreeting}">${greeting}</div>
      ${spacer(20)}
      <div style="${ST.heroTeaser}">${heroTeaser(ff, opts.gender)}</div>
      ${spacer(40)}
      <div style="${ST.heroScroll}">Scroll to learn all about your feet<br>v</div>
      ${spacer(40)}
    `);
  }

  // ── ARCHES (navy) ──────────────────────────────────────────────────────────
  function arches(ff) {
    const L = ff.leftFoot;
    const R = ff.rightFoot;
    const same = L.archHeightClass === R.archHeightClass;
    const summary = same
      ? `You have <strong>${L.archHeightClass}</strong> arches.*`
      : `Your left foot has <strong>${archPhrase(L.archHeightClass)}</strong> arch and your right foot has <strong>${archPhrase(R.archHeightClass)}</strong> arch.*`;

    const facts = [];
    if (ff.archHeightRatio >= 1) {
      facts.push(`Your left arch is <strong>${ff.archHeightRatio}% higher</strong> than your right arch.`);
    } else if (ff.archHeightRatio <= -1) {
      facts.push(`Your left arch is <strong>${Math.abs(ff.archHeightRatio)}% lower</strong> than your right arch.`);
    }
    if (L.archHeightClass === 'high' && R.archHeightClass === 'high') {
      facts.push('Your feet don’t sufficiently absorb shock with every step you take. You have a greater risk of general foot pain and stress on your plantar fascia, putting you at higher risk for plantar fasciitis.');
      facts.push('The top of your foot might push up against the top of your shoe in an uncomfortable way.');
      facts.push('Consider wearing shoes with laces. This lets you loosen the top of your shoes.');
      facts.push('Try shoes that are a half size bigger than your true size.');
    }
    if (L.archHeightClass === 'low' && R.archHeightClass === 'low') {
      facts.push('Your foot is doing more work, putting you at a greater risk of general foot fatigue and chronic conditions like plantar fasciitis and achilles tendonitis.');
      facts.push('Buy shoes that have stretchy or flexible sides and tops.');
    }
    if (L.archHeightClass === 'average' && R.archHeightClass === 'average') {
      facts.push('Even though your arch height is average, your left and right arches have different support needs.');
    }
    if (!same) {
      facts.push('Choose shoes with laces, velcro, and stretchy or flexible “uppers” (sides and top of the shoe) so they are easy to adjust to get the best fit for each of your feet.');
      if (L.archHeightClass === 'high' || R.archHeightClass === 'high') {
        facts.push('Try shoes that are a half size bigger than your true size.');
      }
    }

    return section('#2a3f7a', `
      ${spacer(40)}
      <div style="${ST.sectionTitle}">Your Arches</div>
      <div style="${ST.summary}">${summary}</div>
      ${spacer(40)}
      ${footCards({
        imgLeft: `${S3}/foot/sideLeft-arch-${L.archHeightClass}.png`,
        imgRight: `${S3}/foot/sideRight-arch-${R.archHeightClass}.png`,
        imgMaxWidth: 180,
        arrowColor: 'green',
        leftPct: L.archHeightPercentile,
        rightPct: R.archHeightPercentile,
        scaleTop: 'High',
        scaleBottom: 'Low',
        captionLeft: `${capfirst(L.archHeightClass)}<br>${L.archHeight} mm`,
        captionRight: `${capfirst(R.archHeightClass)}<br>${R.archHeight} mm`,
      })}
      ${spacer(40)}
      <div style="${ST.subhead}">Get to know your arches:</div>
      ${spacer(20)}
      ${bullets(facts)}
      <div style="${ST.footnote}">* Note: Your arch height class (high, average or low) is based on the ratio between the length of your foot and height of your arch.</div>
      ${spacer(60)}
    `);
  }

  // ── FOOT WIDTH (green) ─────────────────────────────────────────────────────
  function width(ff) {
    const L = ff.leftFoot;
    const R = ff.rightFoot;
    const same = L.widthClass === R.widthClass;
    const summary = same
      ? `You’ve got <strong>${L.widthClass !== 'average' ? L.widthClass : 'average width'}</strong> feet.`
      : `You’ve got <strong>${widthPhrase(L.widthClass)}</strong> left foot and <strong>${widthPhrase(R.widthClass)}</strong> right foot.`;

    const facts = [];
    if (ff.widthRatio >= 1) {
      facts.push(`Your left foot is <strong>${ff.widthRatio}% wider</strong> than your right foot.`);
    } else if (ff.widthRatio <= -1) {
      facts.push(`Your left foot is <strong>${Math.abs(ff.widthRatio)}% narrower</strong> than your right foot.`);
    }
    if (L.widthClass === 'wide' && R.widthClass === 'wide') {
      facts.push('You’re at greater risk of developing bunions, especially if you wear shoes with pointed or narrow toes.');
    }
    if (L.widthClass === 'narrow' && R.widthClass === 'narrow') {
      facts.push('You may find your feet constantly moving/slipping around inside your shoes which can make you feel more clumsy and prone to hot feet and blistering.');
    }
    if (L.widthClass === 'average' && R.widthClass === 'average') {
      facts.push('Most shoes are not made with a natural foot shape in mind and instead built off of a rudimentary "shoe last" shape.');
      facts.push('ALWAYS try to find shoes that have more anatomically shaped toe areas so you can enjoy foot comfort and function all day long.');
    }
    if (!same) {
      facts.push(`${ff.sameWidthClassesRatio}% of people have ${widthPhrase(L.widthClass)} left foot and ${widthPhrase(R.widthClass)} right foot.`);
    }
    if (L.widthClass === 'wide' && R.widthClass === 'wide') {
      facts.push('Choose shoes that come in wide widths, and/or use shoe stretchers to widen your shoes.');
    }
    if (L.widthClass === 'narrow' && R.widthClass === 'narrow') {
      facts.push('Choose shoes that come in narrow widths.');
      facts.push('Try using a custom insole as these can help achieve a better fit within your shoe by filling in some of the negative space.');
    }
    if (!same) {
      if (L.widthClass === 'wide' || R.widthClass === 'wide') {
        facts.push('Since one of your feet is wide, choose wide shoe styles and use custom insoles. They help achieve a better fit within your shoe by filling in some of the negative space.');
      } else if (L.widthClass === 'narrow' || R.widthClass === 'narrow') {
        facts.push('Choose standard width shoes and use an insole to help fill the negative space in the shoe for your narrow foot.');
      }
    }

    return section('#68be65', `
      ${spacer(40)}
      <div style="${ST.sectionTitle}">Foot Width</div>
      <div style="${ST.summary}">${summary}</div>
      ${spacer(40)}
      ${footCards({
        imgLeft: `${S3}/foot/topLeft-width-${L.widthClass}.png`,
        imgRight: `${S3}/foot/topRight-width-${R.widthClass}.png`,
        imgMaxWidth: 100,
        arrowColor: 'yellow',
        leftPct: L.widthPercentile,
        rightPct: R.widthPercentile,
        scaleTop: 'Wide',
        scaleBottom: 'Narrow',
        captionLeft: `${capfirst(L.widthClass)}<br>${L.width} mm`,
        captionRight: `${capfirst(R.widthClass)}<br>${R.width} mm`,
      })}
      ${spacer(40)}
      <div style="${ST.subhead}">About your foot width</div>
      ${spacer(20)}
      ${bullets(facts)}
      ${spacer(40)}
    `);
  }

  // ── FOOT LENGTH (red) ──────────────────────────────────────────────────────
  function lengthSection(ff, gender) {
    const L = ff.leftFoot;
    const R = ff.rightFoot;
    let summary;
    if (sizesDiffer(ff, gender)) {
      if (gender === 'male') {
        summary = `Your left foot is <strong>a men’s</strong> size <strong>${L.shoeSizes.usMen}</strong> and your right foot is a <strong>men’s</strong> size <strong>${R.shoeSizes.usMen}</strong>`;
      } else if (gender === 'female') {
        summary = `Your left foot is <strong>a women’s</strong> size <strong>${L.shoeSizes.usWomen}</strong> and your right foot is a <strong>women’s</strong> size <strong>${R.shoeSizes.usWomen}</strong>`;
      } else {
        summary = `Your left foot is <strong>a men’s / women’s</strong> size <strong>${L.shoeSizes.usMen} / ${L.shoeSizes.usWomen}</strong> and your right foot is a <strong>men’s / women’s</strong> size <strong>${R.shoeSizes.usMen} / ${R.shoeSizes.usWomen}</strong>`;
      }
    } else if (L.length === R.length) {
      summary = 'Your feet are EXACTLY the same length';
    } else if (ff.lengthRatio > 0) {
      summary = `Your left foot is ${ff.lengthRatio}% longer than your right foot.`;
    } else {
      summary = `Your left foot is ${Math.abs(ff.lengthRatio)}% shorter than your right foot.`;
    }

    const lengthAdj = (cls) =>
      cls === 'above_average' ? 'a longer' : cls === 'average' ? 'an average' : 'a shorter';
    const facts = [];
    if (L.lengthClass === R.lengthClass) {
      facts.push(
        L.lengthClass === 'below_average'
          ? 'You’ve got shorter feet compared to most people.'
          : L.lengthClass === 'above_average'
            ? 'You’ve got longer feet compared to most people.'
            : 'You’ve got average length feet compared to most people.'
      );
    } else {
      facts.push(`Your left foot is ${lengthAdj(L.lengthClass)} length and your right foot is ${lengthAdj(R.lengthClass)} length compared to most people.`);
    }
    if (sizesDiffer(ff, gender)) {
      facts.push('21% of people have feet that are different shoe sizes.');
    }

    return section('#be5668', `
      ${spacer(40)}
      <div style="${ST.sectionTitle}">Foot Length</div>
      <div style="${ST.summary}">${summary}</div>
      ${spacer(40)}
      ${footCards({
        imgLeft: `${S3}/foot/topLeft-length-${L.lengthClass}.png`,
        imgRight: `${S3}/foot/topRight-length-${R.lengthClass}.png`,
        imgMaxWidth: 100,
        arrowColor: 'red',
        leftPct: L.lengthPercentile,
        rightPct: R.lengthPercentile,
        scaleTop: 'Above<br>Average',
        scaleBottom: 'Below<br>Average',
        captionLeft: `${lengthCaption(L.lengthClass)}<br>${L.length} mm`,
        captionRight: `${lengthCaption(R.lengthClass)}<br>${R.length} mm`,
      })}
      ${spacer(40)}
      <div style="${ST.subhead}">About your foot length</div>
      ${spacer(20)}
      ${bullets(facts)}
      ${spacer(60)}
    `);
  }

  // ── SIZE CHART + TIPS (grey) ───────────────────────────────────────────────
  function chart(ff, gender) {
    const parts = [];
    if (sizesDiffer(ff, gender)) {
      let note;
      if (gender === 'male') {
        note = `Your left foot is a men’s size ${ff.leftFoot.shoeSizes.usMen} and your right foot is a men’s size ${ff.rightFoot.shoeSizes.usMen}. You will want to buy shoes that fit your largest foot.`;
      } else if (gender === 'female') {
        note = `Your left foot is a women’s size ${ff.leftFoot.shoeSizes.usWomen} and your right foot is a women’s size ${ff.rightFoot.shoeSizes.usWomen}. You will want to buy shoes that fit your largest foot.`;
      } else {
        note = 'Your left and right feet are different shoe sizes. You should buy shoes that fit your largest foot.';
      }
      parts.push(`<div style="${ST.chartLine}">${note}</div>`, spacer(20));
    }
    if (gender === 'male') {
      parts.push(`<div style="${ST.chartLine}"><strong>Mens - US: ${maxOf(ff, 'usMen')}</strong></div>`);
      parts.push(`<div style="${ST.chartLine}"><strong>Mens - UK: ${maxOf(ff, 'uk')}</strong></div>`);
    } else if (gender === 'female') {
      parts.push(`<div style="${ST.chartLine}"><strong>Womens - US: ${maxOf(ff, 'usWomen')}</strong></div>`);
      parts.push(`<div style="${ST.chartLine}"><strong>Womens - UK: ${maxOf(ff, 'uk')}</strong></div>`);
    } else {
      parts.push(`<div style="${ST.chartLine}">US: men’s size ${maxOf(ff, 'usMen')} / women’s size ${maxOf(ff, 'usWomen')}</div>`);
      // D11 bug-2 fix: the email fabricates a "women's" UK size from the men's uk
      // field here; there is no women's UK value in the data, so show the single
      // unisex UK size instead.
      parts.push(`<div style="${ST.chartLine}">UK: size ${maxOf(ff, 'uk')}</div>`);
    }

    const tips = bullets([
      'Buy shoes mid-day, before vigorous exercise.',
      'Look for shoes with laces, velcro, stretchy, flexible shoe sides and tops.',
      'Factory inserts are where shoe companies cut costs. If your feet are tired after you stand or walk for a couple of hours, upgrade to custom insoles.',
    ]);

    return section('#7c8181', `
      ${spacer(40)}
      <div style="${ST.chartTitle}">International Shoe Size Chart</div>
      ${spacer(40)}
      <div style="${ST.subhead}">Your shoe size</div>
      ${spacer(10)}
      ${parts.join('')}
      ${spacer(40)}
      <div style="${ST.subhead}">Tips</div>
      ${spacer(10)}
      ${tips}
      ${spacer(20)}
    `);
  }

  // ── Entry point ────────────────────────────────────────────────────────────
  function render(footFacts, options) {
    const opts = options || {};
    if (!footFacts || !footFacts.leftFoot || !footFacts.rightFoot) {
      throw new Error('FootFactsRenderEmail.render: a computed footFacts object is required');
    }
    const gender = opts.gender === 'male' || opts.gender === 'female' ? opts.gender : null;
    return `
      <div class="foot-facts-report foot-facts-report--email" style="max-width:620px; margin:0 auto; background-color:#fff; word-break:break-word">
        ${hero(footFacts, { gender, firstName: opts.firstName })}
        ${arches(footFacts)}
        ${width(footFacts)}
        ${lengthSection(footFacts, gender)}
        ${chart(footFacts, gender)}
      </div>`;
  }

  window.FootFactsRenderEmail = { render };
})();
